/*
 * The labels sit above their field wrapper rather than around it — the password
 * box also holds the Show/Hide button, and nesting a button inside a <label>
 * makes every click on it double as a label activation. Each label still names
 * its input through htmlFor, which is what assistive technology reads.
 */
/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useEffect, useRef, useState } from 'react';
import { Redirect } from 'react-router-dom';

// @material-ui/core components
import { makeStyles } from '@material-ui/core/styles';

import markWhite from 'assets/img/logo-mark-white.png';
import wordmarkWhite from 'assets/img/logo-wordmark-white.png';
import styles from 'assets/jss/material-dashboard-react/views/loginStyle';

import { login } from 'services/session';

const useStyles = makeStyles(styles);

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const SITE_URL = 'https://fluvius.co';

const INVOICES = [
  { id: 'INV-2026-041', status: 'Paid', paid: true },
  { id: 'INV-2026-042', status: 'Due 14d', paid: false },
];

const HIGHLIGHTS = [
  {
    n: '01',
    title: 'Invoices & contracts',
    text: 'Every number on our business cooperation calculated.',
  },
  {
    n: '02',
    title: 'Your investment, plan and actual',
    text: 'Every month so far and the forecast ahead, always up to date.',
  },
  {
    n: '03',
    title: 'Team & time off',
    text: 'Who is on your team and their calendar.',
  },
];

// Six months booked, the current one highlighted, two forecast.
const CHART_BARS = [
  { height: '38%' },
  { height: '56%' },
  { height: '47%' },
  { height: '72%' },
  { height: '64%' },
  { height: '90%', kind: 'now' },
  { height: '54%', kind: 'fc' },
  { height: '62%', kind: 'fc' },
];

const GENERIC_ERROR = 'Sign in failed. Please try again.';

/*
 * utils/http rejects with the parsed JSON body rather than an Error, so a
 * failed sign-in arrives in one of three shapes: an Error thrown by login()'s
 * own empty-field guards, passport's `{ message }`, or express-validator's
 * `{ errors: [{ msg }] }`.
 */
export function extractError(error) {
  if (!error) {
    return GENERIC_ERROR;
  }

  if (error.message) {
    return error.message;
  }

  if (Array.isArray(error.errors) && error.errors.length && error.errors[0].msg) {
    return error.errors[0].msg;
  }

  return GENERIC_ERROR;
}

function Brand() {
  const classes = useStyles();

  return (
    <a className={classes.brand} href={SITE_URL} target="_blank" rel="noopener noreferrer">
      <img className={classes.mark} src={markWhite} alt="" decoding="async" />
      <img className={classes.word} src={wordmarkWhite} alt="Fluvius" decoding="async" />
      <span className={classes.tag}>Workspace</span>
    </a>
  );
}

function Preview() {
  const classes = useStyles();

  return (
    <div className={classes.preview} aria-hidden="true">
      <div className={`${classes.pvCard} ${classes.pvTop}`}>
        <div className={classes.pvGrow}>
          <div className={classes.pvK}>Invoices</div>
          <div className={classes.pvRow}>
            {INVOICES.map((invoice) => (
              <div className={classes.pvItem} key={invoice.id}>
                {`${invoice.id} · `}
                <span className={invoice.paid ? classes.pvPaid : classes.pvDue}>
                  {invoice.status}
                </span>
              </div>
            ))}
          </div>
        </div>
        <div className={classes.pvRule} />
        <div className={classes.pvTeam}>
          <div className={classes.pvAv} />
          <div>
            <div className={classes.pvTeamName}>Team · 4</div>
            <div className={classes.pvTeamNote}>QA off until 22 Sep</div>
          </div>
        </div>
      </div>

      <div className={`${classes.pvCard} ${classes.pvBot}`}>
        <div className={classes.pvFixed}>
          <div className={classes.pvHead}>
            <div className={classes.pvK}>Investment · Sep</div>
            <div className={classes.pvPlan}>plan $52,000</div>
          </div>
          <div className={classes.pvSum}>$48,200</div>
        </div>
        <div className={classes.pvChart}>
          {CHART_BARS.map((bar, index) => {
            const bars = { now: classes.pvBarNow, fc: classes.pvBarFc };

            return <i className={bars[bar.kind] || classes.pvBar} style={{ height: bar.height }} key={index} />;
          })}
        </div>
      </div>
    </div>
  );
}

export default function Login() {
  const classes = useStyles();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [redirectUrl, setRedirectUrl] = useState('');

  const emailRef = useRef(null);
  const passwordRef = useRef(null);

  const year = new Date().getFullYear();

  useEffect(() => {
    document.title = 'Sign in | Fluvius Client Workspace';

    // Autofocus without the autoFocus attribute, which jsx-a11y forbids.
    emailRef.current.focus();
  }, []);

  // Typing anywhere in the form clears both fields' errors.
  function clearErrors() {
    setEmailError('');
    setPasswordError('');
  }

  function toggleShowPassword() {
    setShowPassword(!showPassword);

    passwordRef.current.focus();
  }

  async function handleSubmit(e) {
    e.preventDefault();

    clearErrors();

    const value = email.trim();

    if (!EMAIL_PATTERN.test(value)) {
      setEmailError('Enter the work email your contract is registered to.');
      emailRef.current.focus();

      return;
    }

    if (!password) {
      // Deliberately generic — never say which half of the pair was wrong.
      setPasswordError('Wrong email or password.');
      passwordRef.current.focus();

      return;
    }

    setLoading(true);

    try {
      await login(value, password);

      setRedirectUrl('/');
    } catch (error) {
      setPasswordError(extractError(error));
    }

    setLoading(false);
  }

  if (redirectUrl) {
    return <Redirect push to={redirectUrl} />;
  }

  return (
    <div className={classes.shell}>

      <div className={classes.panel}>
        <Preview />

        <div className={classes.scrim} aria-hidden="true" />

        <Brand />

        <div className={classes.pitch}>
          <h1 className={classes.pitchTitle}>
            Full transparency
            <span className={classes.dot}>.</span>
          </h1>
          <div className={classes.inside}>
            {HIGHLIGHTS.map((item) => (
              <div className={classes.insideRow} key={item.n}>
                <div className={classes.insideN}>{item.n}</div>
                <div>
                  <div className={classes.insideT}>{item.title}</div>
                  <div className={classes.insideD}>{item.text}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <a className={classes.copyright} href={SITE_URL} target="_blank" rel="noopener noreferrer">
          {`© Fluvius.co 2017—${year}`}
        </a>
      </div>

      <div className={classes.mhead}>
        <Brand />
        <h1 className={classes.mheadTitle}>
          Sign in to your hub
          <span className={classes.dot}>.</span>
        </h1>
      </div>

      <div className={classes.side}>
        <form className={classes.form} onSubmit={handleSubmit} noValidate>
          <div className={classes.kicker}>Sign in</div>
          <h2 className={classes.formTitle}>Client Workspace</h2>
          <p className={classes.lede}>Sign in with the work email your contract is registered to.</p>

          <div className={`${classes.field} ${classes.fieldFirst}`}>
            <label className={classes.lab} htmlFor="email">Work email</label>
            <div className={`${classes.box} ${emailError ? classes.boxBad : ''}`}>
              <input
                className={classes.input}
                id="email"
                name="email"
                type="email"
                inputMode="email"
                autoComplete="username"
                placeholder="you@company.com"
                autoCapitalize="off"
                spellCheck="false"
                ref={emailRef}
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  clearErrors();
                }}
                aria-invalid={!!emailError}
                aria-describedby="err-email"
              />
            </div>
            <div className={`${classes.err} ${emailError ? classes.errOn : ''}`} id="err-email" role="alert">
              {emailError}
            </div>
          </div>

          <div className={classes.field}>
            <label className={classes.lab} htmlFor="password">Password</label>
            <div className={`${classes.box} ${passwordError ? classes.boxBad : ''}`}>
              <input
                className={classes.input}
                id="password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                ref={passwordRef}
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  clearErrors();
                }}
                aria-invalid={!!passwordError}
                aria-describedby="err-password"
              />
              <button
                className={classes.peek}
                type="button"
                onClick={toggleShowPassword}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? 'Hide' : 'Show'}
              </button>
            </div>
            <div className={`${classes.err} ${passwordError ? classes.errOn : ''}`} id="err-password" role="alert">
              {passwordError}
            </div>
          </div>

          <button className={classes.go} type="submit" disabled={loading}>
            {loading ? 'Signing in…' : 'Enter workspace →'}
          </button>

          <div className={classes.foot}>No access yet? Message us in Slack or by email</div>

          <div className={classes.mfoot}>{`© Fluvius.co 2017—${year}`}</div>
        </form>
      </div>

    </div>
  );
}
